// SPDX-FileCopyrightText: Copyright 2025 OP Financial Group (https://op.fi). All Rights Reserved.
//
// SPDX-License-Identifier: LicenseRef-OpAllRightsReserved

import React, { useEffect, useRef, useState } from 'react';
import TupasButton from './TupasButton';
import NavigationButtons from '../../components/NavigationButtons';
import message from '../../messages';
import { Alert, Loading } from '@op/opds.opux';
import UseWindowWidth from '../../hooks/useWindowWidth';
import { pageRoutes } from '../../Constants';
import { ApiResponse } from '../../context/ContextWrapper';
import client from '../../api/RestApi';

const viewThreshold: number = 400;

interface TupasLoginProps {
    originUrl: string;
}

interface IdpProvider {
    name: string;
    imageUrl: string;
    ftn_idp_id: string;
}

const TupasLogin: React.FC<TupasLoginProps> = ({ originUrl }) => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [showAlert, setShowAlert] = useState<boolean>(false);
    const [idps, setIdps] = useState<IdpProvider[]>([]);
    const width: number = UseWindowWidth();
    const hasInitialized = useRef(false);

    useEffect(() => {
        if (process.env.NODE_ENV === 'development') {
            // Use a flag to prevent double execution in React StrictMode
            if (hasInitialized.current) return;
            hasInitialized.current = true;
        }
        client
            .get('/idps')
            .then((response: ApiResponse) => {
                if (response.status === 'FAILURE') {
                    window.location.hash = pageRoutes.error;
                } else {
                    setIdps(JSON.parse(response.body ?? '{}'));
                    setIsLoading(false);
                }
            })
            .catch(() => {
                window.location.hash = pageRoutes.error;
            });
    }, []);

    const openTupas = (ftn_idp_id: string): void => {
        const parsedUrl = '/tupas?ftn_idp_id=' + ftn_idp_id + '&origin=' + originUrl;
        client
            .get(parsedUrl)
            .then((response: ApiResponse) => {
                if (response.status === 'SUCCESS') {
                    window.location.href = response.body ?? '';
                } else {
                    setShowAlert(true);
                }
            })
            .catch(() => setShowAlert(true));
    };

    return (
        <div className="opux-align-center">
            <div className="opux-spacing-bottom-margin-double opux-spacing-top-margin-double">
                <div
                    className={
                        width > viewThreshold
                            ? 'heading-margin-top-150 opux-spacing-bottom-margin-double'
                            : 'opux-spacing-top-margin-single opux-spacing-bottom-margin-double'
                    }
                >
                    <h2>{message('tupasLoginHeading')}</h2>
                </div>
                {showAlert && (
                    <div className="opux-spacing-top-margin-double opux-align-center">
                        <Alert type="critical">{message('tupasLoginFailed')}</Alert>
                    </div>
                )}
                {!isLoading ? (
                    <>
                        <div className="opux-g-row opux-tupas-grid opux-spacing-bottom-margin-double">
                            {idps.map(({ name, imageUrl, ftn_idp_id }) => (
                                <TupasButton
                                    key={ftn_idp_id}
                                    name={name}
                                    image={imageUrl}
                                    onClick={() => openTupas(ftn_idp_id)}
                                />
                            ))}
                        </div>{' '}
                        <NavigationButtons backPath="/preview" cancelEnabled />
                    </>
                ) : (
                    <div className="opux-spacing-top-margin-double opux-align-center">
                        <Loading data-testid="loading-spinner" />
                    </div>
                )}
            </div>
        </div>
    );
};

export default TupasLogin;
